import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../lib/usercontext';
import ExternalLayout from '../components/layouts/externallayout';
import ErrorMessage from '../components/common/errormessage';
import SuccessMessage from '../components/common/successmessage';
import GeneralTextBox from './common/textboxes/textbox_general';
import PhoneTextBox from './common/textboxes/textbox_phone';
import GeneralButton from './common/button_general';
import Spinner from './common/spinner';
import { getHeaders } from '../lib/requests';

const generateRandomPassword = () => {
    const length = 12; // Length of the password
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
    let password = "";
    for (let i = 0, n = charset.length; i < length; ++i) {
        password += charset.charAt(Math.floor(Math.random() * n));
    }
    return password;
};

const RegistrationForm = () => {
    const { isLoggedIn } = useContext(UserContext);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        // Redirect user to dashboard if already logged in
        if (isLoggedIn) {
            navigate('/dashboard');
        }
    }, [isLoggedIn, navigate]);

    const pageTitle = 'Customer Registration';

    const initialPassword = generateRandomPassword();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        username: '',
        email: '',
        temporaryPassword: initialPassword,
        client_id: searchParams.get('client_id') || ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const apiUrl = `${process.env.REACT_APP_API_URL}/register`; // Updated endpoint
        try {
            const response = await axios.post(apiUrl, formData, { headers: getHeaders() });
            if (response.status === 201) {
                setShowSuccess(true);
                setSuccessMessage('Registration successful. Please check your email for verification.');
                navigate('/login?registered=true');
            }
            else {
                setShowError(true);
                setErrorMessage('An error occurred during registration, please try again.');
            }
        } catch (error) {
            setShowError(true);
            setErrorMessage(error.response?.data?.message || 'An error occurred during registration.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ExternalLayout pageTitle={pageTitle}>
            {showError && <ErrorMessage message={errorMessage} />}
            {showSuccess && <SuccessMessage message={successMessage} />}
            {isLoading ? <Spinner /> : (
                <form onSubmit={handleSubmit} className="p-4">
                    <GeneralTextBox
                        label="First Name" id="name" name="first_name"
                        value={formData.first_name} onChange={handleChange}
                        placeholder="First Name" required />
                    <GeneralTextBox
                        label="Last Name" id="last_name" name="last_name"
                        value={formData.last_name} onChange={handleChange}
                        placeholder="Last Name" required />
                    <PhoneTextBox
                        label="Phone Number" id="phone_number" name="phone_number"
                        value={formData.phone_number} onChange={handleChange}
                        placeholder="Phone Number" s/>
                    <GeneralTextBox
                        label="Username" id="username" name="username"
                        value={formData.username} onChange={handleChange}
                        placeholder="Username" required
                    />
                    <GeneralTextBox
                        label="Email" id="email" name="email"
                        value={formData.email} onChange={handleChange}
                        placeholder="Email" required
                    />
                    <GeneralTextBox
                        label="Password" id="password" name="password" type="password"
                        value={formData.password} onChange={handleChange}
                        placeholder="Password" required
                    />
                    <GeneralTextBox
                        label="Confirm Password" id="confirm_password" name="confirm_password" type="password"
                        value={formData.confirm_password} onChange={handleChange}
                        placeholder="Confirm Password" required />
                    <div className="flex justify-center mt-6">
                        <GeneralButton type="submit" buttonType="primary">
                            Register
                        </GeneralButton>
                    </div>
                </form>
            )}
        </ExternalLayout>
    );
};

RegistrationForm.propTypes = {
    onLoginSuccess: PropTypes.func
};

export default RegistrationForm;
